
import { useState } from 'react';
import { useLocation } from 'react-router';
import { useTranslations } from '@/i18n/use-translations';
import { useAuth } from '@/contexts/auth-context';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { MessageSquare, X, Send } from 'lucide-react';

type FeedbackType = 'bug' | 'suggestion' | 'question';

const typeKeys: Record<FeedbackType, string> = {
  bug: 'bug',
  suggestion: 'suggestion',
  question: 'question',
};

export function FeedbackWidget() {
  const { isAuthenticated, admin } = useAuth();
  const t = useTranslations('feedback');
  const tc = useTranslations('common');
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<FeedbackType>('suggestion');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  // Pages with a bottom composer (agent Probar chat): lift the button so it never covers "Enviar".
  const { pathname } = useLocation();
  const liftOnMobile = /^\/agents\/[^/]+\/test$/.test(pathname);

  if (!isAuthenticated) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    setSending(true);
    try {
      await api.feedback.submit({
        type,
        message: message.trim(),
        email: admin?.email,
        pageUrl: window.location.href,
      });
      setSent(true);
      setTimeout(() => {
        setOpen(false);
        setSent(false);
        setMessage('');
        setType('suggestion');
      }, 2000);
    } catch {
      alert(t('errorSend'));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className={`fixed ${liftOnMobile ? 'bottom-36 lg:bottom-4' : 'bottom-4'} right-4 z-50 flex items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground shadow-lg hover:bg-primary/90 transition-colors`}
        >
          <MessageSquare className="h-4 w-4" />
          {tc('feedback')}
        </button>
      )}

      {/* Modal */}
      {open && (
        <div className="fixed bottom-4 right-4 left-4 sm:left-auto z-50 sm:w-80 rounded-lg border bg-background shadow-xl">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <span className="text-sm font-medium">{t('title')}</span>
            <button
              onClick={() => setOpen(false)}
              className="rounded-md p-1 hover:bg-accent"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {sent ? (
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-muted-foreground">
                {t('thanks')}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-4 space-y-3">
              {/* Type selector */}
              <div className="flex gap-1">
                {(Object.keys(typeKeys) as FeedbackType[]).map((ft) => (
                  <button
                    key={ft}
                    type="button"
                    onClick={() => setType(ft)}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      type === ft
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                    }`}
                  >
                    {t(ft)}
                  </button>
                ))}
              </div>

              {/* Message */}
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('placeholder')}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                rows={4}
                required
              />

              {/* Email (pre-filled) */}
              <input
                type="email"
                value={admin?.email || ''}
                disabled
                className="w-full rounded-md border bg-muted px-3 py-2 text-sm text-muted-foreground"
              />

              {/* Submit */}
              <Button
                type="submit"
                disabled={sending || !message.trim()}
                className="w-full"
                size="sm"
              >
                <Send className="h-3.5 w-3.5" />
                {sending ? tc('sending') : t('submit')}
              </Button>
            </form>
          )}
        </div>
      )}
    </>
  );
}
